// src/features/dev/RolePermissionViewer.jsx
import { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Badge,
  Row,
  Col,
  Tab,
  Tabs
} from 'react-bootstrap';
import {
  Wrench,
  Search,
  Users,
  Shield,
  ChevronDown,
  Package,
  DollarSign,
  BarChart3,
  UsersRound,
  Layers,
  Eye,
  CheckCircle,
  XCircle,
  Info,
  RefreshCw,
  ListChecks,
  Blocks
} from 'lucide-react';
import { devService } from '../../services';
import './RolePermissionViewer.css';

// Permission icon map
const getPermissionIcon = (permissionName) => {
  if (!permissionName) return Shield;
  const name = permissionName.toLowerCase();
  if (name.includes('inventory')) return Package;
  if (name.includes('payroll')) return DollarSign;
  if (name.includes('report')) return BarChart3;
  if (name.includes('hr') || name.includes('human')) return UsersRound;
  return Shield;
};

// Feature icon map
const getFeatureIcon = (featureName) => {
  if (!featureName) return Layers;
  const name = featureName.toLowerCase();
  if (name.includes('inventory')) return Package;
  if (name.includes('payroll')) return DollarSign;
  if (name.includes('report')) return BarChart3;
  if (name.includes('hr') || name.includes('human')) return UsersRound;
  return Layers;
};

export default function RolePermissionViewer() {
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [roles, setRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [permissionsGrouped, setPermissionsGrouped] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('permissions');

  // Load companies on mount
  useEffect(() => {
    loadCompanies();
  }, []);

  // Load roles when company changes
  useEffect(() => {
    if (selectedCompanyId) {
      loadRoles(selectedCompanyId);
      loadFeatures(selectedCompanyId);
    } else {
      setRoles([]);
      setSelectedRoleId('');
      setPermissionsGrouped([]);
      setFeatures([]);
    }
  }, [selectedCompanyId]);

  // Load permissions when role changes
  useEffect(() => {
    if (selectedRoleId && roles.length > 0) {
      loadPermissionsForRole(selectedRoleId);
    } else {
      setPermissionsGrouped([]);
    }
  }, [selectedRoleId, roles]);

  const loadCompanies = async () => {
    setLoadingCompanies(true);
    setError('');
    try {
      const result = await devService.getCompanies();
      if (result.success) {
        setCompanies(result.data || []);
        if (result.data && result.data.length > 0) {
          setSelectedCompanyId(result.data[0].id);
        }
      } else {
        setError(result.message || 'Failed to load companies');
      }
    } catch (err) {
      setError('Failed to load companies. Please try again.');
    }
    setLoadingCompanies(false);
  };

  const loadRoles = async (companyId) => {
    setLoadingRoles(true);
    setError('');
    setSelectedRoleId('');
    setPermissionsGrouped([]);
    try {
      const result = await devService.getRolePermissionsByCompany(companyId);
      if (result.success && result.data) {
        const roleData = result.data.roles || [];
        setRoles(roleData);
        if (roleData.length > 0) {
          setSelectedRoleId(roleData[0].role.uuid);
        }
      } else {
        setError(result.message || 'Failed to load roles');
      }
    } catch (err) {
      setError('Failed to load roles. Please try again.');
    }
    setLoadingRoles(false);
  };

  const loadPermissionsForRole = (roleId) => {
    const roleData = roles.find(r => r.role.uuid === roleId);
    if (roleData) {
      setPermissionsGrouped(roleData.permissions_grouped || []);
    } else {
      setPermissionsGrouped([]);
    }
  };

  const loadFeatures = async (companyId) => {
    setLoadingFeatures(true);
    setError('');
    try {
      const result = await devService.getCompanyFeatures(companyId);
      if (result.success && result.data) {
        setFeatures(result.data.features || []);
      } else {
        setError(result.message || 'Failed to load features');
      }
    } catch (err) {
      setError('Failed to load features. Please try again.');
    }
    setLoadingFeatures(false);
  };

  const handleCompanyChange = (e) => {
    const companyId = parseInt(e.target.value);
    setSelectedCompanyId(companyId);
  };

  const handleRoleChange = (e) => {
    const roleId = e.target.value;
    setSelectedRoleId(roleId);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleRefresh = () => {
    if (selectedCompanyId) {
      loadRoles(selectedCompanyId);
      loadFeatures(selectedCompanyId);
    }
  };

  // Filter permissions based on search
  const filteredPermissionsGrouped = useMemo(() => {
    if (!searchTerm.trim()) return permissionsGrouped;
    
    return permissionsGrouped
      .map(group => ({
        ...group,
        permissions: group.permissions.filter(p =>
          p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.code?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      }))
      .filter(group => group.permissions.length > 0);
  }, [permissionsGrouped, searchTerm]);

  // Filter features based on search
  const filteredFeatures = useMemo(() => {
    if (!searchTerm.trim()) return features;
    return features.filter(f =>
      f.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.code?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [features, searchTerm]);

  // Calculate total permissions count
  const totalPermissions = permissionsGrouped.reduce(
    (sum, group) => sum + (group.permissions?.length || 0), 0
  );

  const selectedCompany = companies.find(c => c.id === selectedCompanyId);
  const selectedRole = roles.find(r => r.role?.uuid === selectedRoleId);
  const selectedRoleData = selectedRole?.role || null;

  return (
    <div className="rpv-page">
      <div className="rpv-shell">
        {/* Header */}
        <div className="rpv-header">
          <div className="rpv-header-left">
            <div className="rpv-header-icon">
              <Wrench size={22} color="white" strokeWidth={2.25} />
            </div>
            <div>
              <span className="rpv-eyebrow">Developer Tools</span>
              <h1 className="rpv-title">Role & Permission Viewer</h1>
              <p className="rpv-subtitle">Select a company to view roles, permissions, and features</p>
            </div>
          </div>
          <div className="rpv-header-right">
            <button onClick={handleRefresh} className="rpv-refresh-btn" disabled={loadingRoles || loadingFeatures}>
              <RefreshCw size={16} className={(loadingRoles || loadingFeatures) ? 'rpv-spin' : ''} />
              Refresh
            </button>
            <span className="rpv-dev-badge">
              <Wrench size={12} />
              DEV TOOL
            </span>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="danger" className="rpv-alert rpv-alert-error">
            <XCircle size={18} />
            {error}
          </Alert>
        )}

        {/* Company Selector - Always visible */}
        <div className="rpv-company-bar">
          <div className="rpv-company-selector">
            <label className="rpv-company-label">Select Company</label>
            <div className="rpv-select-wrapper rpv-company-select-wrapper">
              <Form.Select
                value={selectedCompanyId}
                onChange={handleCompanyChange}
                className="rpv-select"
                disabled={loadingCompanies || companies.length === 0}
              >
                {loadingCompanies ? (
                  <option>Loading companies...</option>
                ) : companies.length === 0 ? (
                  <option>No companies found</option>
                ) : (
                  companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))
                )}
              </Form.Select>
              <ChevronDown size={16} className="rpv-select-chevron" />
            </div>
          </div>
          {selectedCompany && (
            <div className="rpv-company-info">
              <span className="rpv-company-name">{selectedCompany.name}</span>
              <span className="rpv-company-id">ID: {selectedCompany.id}</span>
            </div>
          )}
        </div>

        {/* Tabs */}
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="rpv-tabs"
        >
          <Tab eventKey="permissions" title={<><Shield size={14} /> Permissions</>}>
            <div className="rpv-layout">
              {/* Left Column - Role Selector */}
              <div className="rpv-selectors">
                <Card className="rpv-panel">
                  <Card.Body className="rpv-panel-body">
                    {/* Role Selector */}
                    <div className="rpv-field">
                      <label className="rpv-field-label">Select Role</label>
                      <div className="rpv-select-wrapper">
                        <Form.Select
                          value={selectedRoleId}
                          onChange={handleRoleChange}
                          className="rpv-select"
                          disabled={!selectedCompanyId || loadingRoles || roles.length === 0}
                        >
                          {!selectedCompanyId ? (
                            <option>Select a company first</option>
                          ) : loadingRoles ? (
                            <option>Loading roles...</option>
                          ) : roles.length === 0 ? (
                            <option>No roles found</option>
                          ) : (
                            roles.map((role) => (
                              <option key={role.role.uuid} value={role.role.uuid}>
                                {role.role.name}
                              </option>
                            ))
                          )}
                        </Form.Select>
                        <ChevronDown size={16} className="rpv-select-chevron" />
                      </div>
                    </div>

                    {/* Summary Stats */}
                    <div className="rpv-summary">
                      <div className="rpv-summary-item">
                        <span className="rpv-summary-label">Role</span>
                        <span className="rpv-summary-value">
                          {selectedRoleData ? selectedRoleData.name : '—'}
                        </span>
                      </div>
                      <div className="rpv-summary-item">
                        <span className="rpv-summary-label">Permissions</span>
                        <span className="rpv-summary-value">{totalPermissions}</span>
                      </div>
                      <div className="rpv-summary-item">
                        <span className="rpv-summary-label">Type</span>
                        <span className="rpv-summary-value">
                          {selectedRoleData?.is_system_role ? (
                            <Badge className="rpv-badge-system">System</Badge>
                          ) : (
                            <Badge className="rpv-badge-custom">Custom</Badge>
                          )}
                        </span>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>

              {/* Right Column - Permissions Display */}
              <div className="rpv-permissions">
                <Card className="rpv-panel">
                  <Card.Body className="rpv-panel-body">
                    {!selectedCompanyId ? (
                      <div className="rpv-empty-state">
                        <Users size={48} className="rpv-empty-icon" />
                        <p className="rpv-empty-title">Select a company</p>
                        <p className="rpv-empty-subtitle">Choose a company to view its roles</p>
                      </div>
                    ) : !selectedRoleId ? (
                      <div className="rpv-empty-state">
                        <Shield size={48} className="rpv-empty-icon" />
                        <p className="rpv-empty-title">Select a role</p>
                        <p className="rpv-empty-subtitle">Choose a role to view its permissions</p>
                      </div>
                    ) : (
                      <>
                        {/* Role Info Card */}
                        {selectedRoleData && (
                          <div className="rpv-role-card">
                            <div className="rpv-role-header">
                              <div className="rpv-role-info">
                                <h2 className="rpv-role-name">{selectedRoleData.name}</h2>
                                <p className="rpv-role-description">
                                  {selectedRoleData.description || 'No description provided'}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Search Bar */}
                        <div className="rpv-toolbar">
                          <div className="rpv-search">
                            <Search size={17} className="rpv-search-icon" />
                            <input
                              type="text"
                              placeholder="Search permissions by name or code..."
                              value={searchTerm}
                              onChange={handleSearch}
                              className="rpv-search-input"
                              disabled={totalPermissions === 0}
                            />
                            {searchTerm && (
                              <button className="rpv-search-clear" onClick={clearSearch}>
                                <XCircle size={16} />
                              </button>
                            )}
                          </div>
                          <span className="rpv-permission-count">
                            <Shield size={14} />
                            {filteredPermissionsGrouped.reduce(
                              (sum, group) => sum + group.permissions.length, 0
                            )} permission{totalPermissions !== 1 ? 's' : ''}
                          </span>
                        </div>

                        {/* Permissions List - Grouped by Feature */}
                        {filteredPermissionsGrouped.length === 0 ? (
                          <div className="rpv-empty-state rpv-empty-small">
                            {searchTerm ? (
                              <>
                                <Search size={32} className="rpv-empty-icon" />
                                <p className="rpv-empty-title">No matching permissions</p>
                                <p className="rpv-empty-subtitle">Try adjusting your search</p>
                              </>
                            ) : (
                              <>
                                <Shield size={32} className="rpv-empty-icon" />
                                <p className="rpv-empty-title">No permissions assigned</p>
                                <p className="rpv-empty-subtitle">This role has no permissions</p>
                              </>
                            )}
                          </div>
                        ) : (
                          <div className="rpv-permission-groups">
                            {filteredPermissionsGrouped.map((group, groupIndex) => {
                              const Icon = getPermissionIcon(group.feature?.name);
                              return (
                                <div key={groupIndex} className="rpv-permission-group">
                                  <div className="rpv-feature-header">
                                    <div className="rpv-feature-icon">
                                      <Icon size={16} strokeWidth={2} />
                                    </div>
                                    <span className="rpv-feature-name">{group.feature?.name || 'Unknown'}</span>
                                    <span className="rpv-feature-count">
                                      {group.permissions.length} permission{group.permissions.length !== 1 ? 's' : ''}
                                    </span>
                                  </div>
                                  <div className="rpv-permission-grid">
                                    {group.permissions.map((permission, index) => {
                                      const PermIcon = getPermissionIcon(permission.name);
                                      return (
                                        <div
                                          key={permission.uuid}
                                          className="rpv-permission-chip"
                                          style={{ animationDelay: `${index * 0.03}s` }}
                                        >
                                          <div className="rpv-permission-chip-icon">
                                            <PermIcon size={16} strokeWidth={2} />
                                          </div>
                                          <div className="rpv-permission-chip-content">
                                            <span className="rpv-permission-chip-name">
                                              {permission.name}
                                            </span>
                                            <span className="rpv-permission-chip-code">
                                              {permission.code}
                                            </span>
                                          </div>
                                          <div className="rpv-permission-chip-check">
                                            <CheckCircle size={14} />
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Footer */}
                        <div className="rpv-footer">
                          <div className="rpv-footer-left">
                            <Info size={14} />
                            <span>Read-only view • {totalPermissions} total permissions</span>
                          </div>
                          <div className="rpv-footer-right">
                            <span className="rpv-footer-stat">
                              <Users size={12} />
                              {roles.find(r => r.role?.uuid === selectedRoleId)?.users_count || 0} users
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </div>
            </div>
          </Tab>

          <Tab eventKey="features" title={<><Blocks size={14} /> Features</>}>
            <div className="rpv-features-tab">
              <Card className="rpv-panel">
                <Card.Body className="rpv-panel-body">
                  {!selectedCompanyId ? (
                    <div className="rpv-empty-state">
                      <Blocks size={48} className="rpv-empty-icon" />
                      <p className="rpv-empty-title">Select a company</p>
                      <p className="rpv-empty-subtitle">Choose a company to view its features</p>
                    </div>
                  ) : loadingFeatures ? (
                    <div className="rpv-loading-state">
                      <Spinner animation="border" className="rpv-loading-spinner" />
                      <p className="rpv-loading-title">Loading features...</p>
                    </div>
                  ) : filteredFeatures.length === 0 ? (
                    <div className="rpv-empty-state">
                      <Blocks size={48} className="rpv-empty-icon" />
                      <p className="rpv-empty-title">No features found</p>
                      <p className="rpv-empty-subtitle">This company has no features</p>
                    </div>
                  ) : (
                    <>
                      {/* Search Bar */}
                      <div className="rpv-toolbar">
                        <div className="rpv-search">
                          <Search size={17} className="rpv-search-icon" />
                          <input
                            type="text"
                            placeholder="Search features by name or code..."
                            value={searchTerm}
                            onChange={handleSearch}
                            className="rpv-search-input"
                          />
                          {searchTerm && (
                            <button className="rpv-search-clear" onClick={clearSearch}>
                              <XCircle size={16} />
                            </button>
                          )}
                        </div>
                        <span className="rpv-permission-count">
                          <Blocks size={14} />
                          {filteredFeatures.length} feature{filteredFeatures.length !== 1 ? 's' : ''}
                        </span>
                      </div>

                      {/* Features Grid */}
                      <div className="rpv-features-grid">
                        {filteredFeatures.map((feature, index) => {
                          const Icon = getFeatureIcon(feature.name);
                          return (
                            <div
                              key={feature.uuid}
                              className="rpv-feature-card"
                              style={{ animationDelay: `${index * 0.05}s` }}
                            >
                              <div className="rpv-feature-card-icon">
                                <Icon size={24} strokeWidth={1.5} />
                              </div>
                              <div className="rpv-feature-card-content">
                                <div className="rpv-feature-card-header">
                                  <h3 className="rpv-feature-card-name">{feature.name}</h3>
                                  <Badge className="rpv-feature-card-badge">Active</Badge>
                                </div>
                                <p className="rpv-feature-card-description">
                                  {feature.description || 'No description provided'}
                                </p>
                                <div className="rpv-feature-card-footer">
                                  <code className="rpv-feature-card-code">{feature.code}</code>
                                  <span className="rpv-feature-card-licensed">
                                    <CheckCircle size={12} /> Licensed
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Footer */}
                      <div className="rpv-footer">
                        <div className="rpv-footer-left">
                          <Info size={14} />
                          <span>Showing {filteredFeatures.length} features</span>
                        </div>
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>
            </div>
          </Tab>
        </Tabs>
      </div>
    </div>
  );
}